import React, { useState, useEffect } from 'react';
import { Button, Grid, Menu, MenuItem, useTheme, Tooltip, IconButton } from '@mui/material';
import { Typography, TextField, DialogContent, DialogTitle, Chip, Alert } from '@mui/material';
import { DoneRounded, CloseRounded } from '@mui/icons-material';
import { RoundedDialog, StandardDialogActions, RichTextField } from '../../../Global/Components';
import { FormatDateFull, highlightElementError } from '../../../Global/Functions';
import StateManager from '../../../Global/StateManager';
import { isArray, isEmpty, isFunction, isNumber } from 'lodash';
import { v4 } from 'uuid';
import { DueDate } from '../../tasks/workflowComponents';
import { blue, green, red, grey } from '@mui/material/colors';
import moment from 'moment';
import AuditReference from '../../tasks/TaskDialog/AuditReference';

const PRIORITIES = [1, 2, 3];
const CHECK_FIELD_PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

export default function ActionDialog({
  open,
  onClose,
  editable,
  initial,
  referenceInfo,
  onResult,
  onStatusChange,
  forCheckField,
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [assignedExternalUsers, setAssignedExternalUsers] = useState([]);
  const [currExternal, setCurrExternal] = useState('');
  const [status, setStatus] = useState('Sent');
  const [priority, setPriority] = useState('');

  useEffect(() => {
    setStatus(initial?.progress ? initial?.progress : initial?.status || 'Sent');
    setTitle(initial?.title || '');
    setDescription(initial?.description || '');
    setDueDate(initial?.dueDate || null);
    setAssignedExternalUsers(isArray(initial?.assignedExternalUsers) ? initial?.assignedExternalUsers : []);
    setPriority(initial?.priority || '');

    if (initial?.dueDate === undefined && isNumber(initial?.daysForTask)) {
      const due = moment().add(initial.daysForTask, 'days').endOf('day').toDate();
      setDueDate(due);
    }
  }, [open, initial]);

  function save() {
    if (!editable) {
      onClose();
      return;
    }

    const assigned = [...assignedExternalUsers];

    if (currExternal) {
      assigned.push(currExternal);
      setAssignedExternalUsers(assigned);
      setCurrExternal('');
    }

    if (isEmpty(assigned)) {
      StateManager.setErrorAlert('Please provide assigned users');
      highlightElementError('action-assigned-users');
      return;
    }

    const result = {
      _id: initial?._id || v4(),
      title,
      description,
      dueDate,
      assignedExternalUsers: assigned,
      preSetActionId: initial?.preSetActionId,
      priority,
      ...(forCheckField && { progress: status }),
    };

    onResult(result);
    onClose();
  }

  function changeActionStatus(status) {
    setStatus(status);
    onStatusChange(status);
  }

  const hideDone = !title;

  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Grid container>
          <Typography variant="h6">Action</Typography>
          {!editable && dueDate && (
            <Typography sx={{ fontWeight: 500, ml: 'auto' }}>Due {FormatDateFull(dueDate)}</Typography>
          )}
        </Grid>
      </DialogTitle>

      <DialogContent>
        {editable && !forCheckField && (
          <Grid container alignItems="center" item>
            <Alert severity="info">
              This task is to be used when creating general actions for an audit that will show on the audit report. If
              you want the task to show in a users workflow, you need to create an app task
            </Alert>
          </Grid>
        )}

        <Grid container alignItems="center" item sx={{ mt: 2 }} wrap="nowrap">
          {editable ? (
            <TextField
              fullWidth
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              id="audit-action-title"
            />
          ) : (
            <Typography variant="h6">{initial?.title}</Typography>
          )}

          <Grid sx={{ ml: 'auto' }} />

          {(!editable || forCheckField) && (
            <Grid sx={{ ml: 1 }}>
              <ActionStatusButton value={status} editable={isFunction(onStatusChange)} onChange={changeActionStatus} />
            </Grid>
          )}
        </Grid>

        {!editable && priority && (
          <Grid container item sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Priority: level {priority}</Typography>
          </Grid>
        )}

        <Grid container item alignItems="center" sx={{ mt: 1 }} id={'action-assigned-users'}>
          <Typography noWrap sx={{ fontWeight: 500 }}>
            {editable ? 'Send to:' : 'Assigned:'}
          </Typography>

          {editable ? (
            assignedExternalUsers.map((value, index) => (
              <Chip
                label={value}
                key={index}
                sx={{ m: 0.5 }}
                onDelete={() => {
                  const remaining = assignedExternalUsers.filter((_, i) => i !== index);
                  setAssignedExternalUsers(remaining);
                }}
              />
            ))
          ) : (
            <>
              {!isEmpty(assignedExternalUsers) &&
                assignedExternalUsers.map((value, index) => <Chip label={value} key={index} sx={{ m: 0.5 }} />)}
            </>
          )}
        </Grid>

        {editable && (
          <Grid container item alignItems="center" sx={{ mt: 1 }}>
            <TextField
              placeholder="Enter assigned user and press 'Enter'"
              style={{ flexGrow: 1, marginRight: 8 }}
              value={currExternal}
              onChange={(e) => {
                setCurrExternal(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && currExternal) {
                  setCurrExternal('');
                  setAssignedExternalUsers([...assignedExternalUsers, currExternal]);
                }
              }}
              variant="standard"
              id="action-assigned-user"
            />

            <Tooltip title="Add" placement="top">
              <IconButton
                onClick={() => {
                  if (!currExternal) return;
                  setCurrExternal('');
                  setAssignedExternalUsers([...assignedExternalUsers, currExternal]);
                }}
              >
                <DoneRounded style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Clear" placement="top">
              <IconButton onClick={() => setCurrExternal('')}>
                <CloseRounded style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        {referenceInfo?.auditId && (
          <AuditReference auditId={referenceInfo.auditId} requirementId={referenceInfo.requirementId} />
        )}

        <Grid container item sx={{ mt: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Action details
          </Typography>
        </Grid>

        <Grid container item>
          <RichTextField
            initial={description}
            editOpen={editable}
            editable={editable}
            onChange={setDescription}
            placeholder={'Description'}
            hideButton
          />
        </Grid>

        {editable && (
          <>
            <Grid container item sx={{ mt: 1 }}>
              <DueDate editable value={dueDate} setValue={setDueDate} />
            </Grid>
            <Grid container item sx={{ mt: 1 }}>
              <TextField
                label="Priority"
                style={{ width: 200 }}
                variant="standard"
                select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {!forCheckField &&
                  PRIORITIES.map((x) => (
                    <MenuItem key={x} value={String(x)}>
                      Level {x}
                    </MenuItem>
                  ))}
                {forCheckField &&
                  CHECK_FIELD_PRIORITIES.map((x) => (
                    <MenuItem key={x} value={String(x)}>
                      {x}
                    </MenuItem>
                  ))}
                <MenuItem value={''}>none</MenuItem>
              </TextField>
            </Grid>
          </>
        )}
      </DialogContent>

      <StandardDialogActions onClose={onClose} onDone={save} hideDone={hideDone} />
    </RoundedDialog>
  );
}

export function ActionStatusButton({ value, editable, onChange, forCheckField, ...props }) {
  const [status, setStatus] = useState('Sent');
  const [statusChangeMenu, setStatusChangeMenu] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    setStatus(value || 'Sent');
  }, [value]);

  const colors = {
    Sent: blue[600],
    Working: blue[600],
    Complete: green[600],
    Overdue: red[600],
    NA: grey[600],
  };

  const background = colors[status];

  const statuses = [forCheckField ? 'NA' : null, 'Sent', 'Working', 'Complete'];

  return (
    <Grid item {...props}>
      <Button
        style={{
          textTransform: 'none',
          background,
          color: background ? theme.palette.getContrastText(background) : '',
          cursor: editable ? '' : 'default',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setStatusChangeMenu(e.currentTarget);
        }}
      >
        {status}
      </Button>

      {editable && (
        <Menu
          anchorEl={statusChangeMenu}
          open={Boolean(statusChangeMenu)}
          onClose={(event) => {
            event.stopPropagation();
            setStatusChangeMenu(null);
          }}
        >
          {statuses.map((x) => (
            <MenuItem
              key={x}
              onClick={(event) => {
                event.stopPropagation();
                setStatusChangeMenu(null);
                setStatus(x);
                isFunction(onChange) && onChange(x);
              }}
              style={{ background: colors[x], color: colors[x] ? theme.palette.getContrastText(colors[x]) : '' }}
            >
              {x}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Grid>
  );
}
